import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function FAQPage() {
  const { theme } = useTheme();

  // Filter FAQ items based on Discord setting
  const faqs = SITE_CONFIG.content.faq.items.filter(item => {
    // Hide Discord-related questions if Discord is disabled
    if (!SITE_CONFIG.display.navbar.discordButton) {
      if (item.question === "How do I become a member?" ||
          item.question === "Are there any membership fees?") {
        return false;
      }
    }
    return true;
  });

  const [openFAQ, setOpenFAQ] = useState(null);
  const contentRefs = useRef([]);

  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, faqs.length);
  }, [faqs.length]);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.primary.bg} ${theme.primary.text}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="p-6 md:p-12 mb-8 text-center"
      >
        <h1 className={`text-5xl font-extrabold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent drop-shadow-2xl mb-4`}>
          {SITE_CONFIG.content.faq.title}
        </h1>
        <p className={`text-xl ${theme.primary.textSecondary} ${theme.primary.card} p-3 rounded-xl border inline-block`}>
          {SITE_CONFIG.content.faq.subtitle}
        </p>
      </motion.header>

      {/* FAQ List */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="p-6 md:p-12 grid gap-6 md:gap-8 max-w-4xl mx-auto"
      >
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className={`${theme.primary.card} rounded-2xl border shadow-2xl overflow-hidden`}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className={`w-full text-left p-6 flex justify-between items-center focus:outline-none hover:${theme.primary.cardHover} transition-all duration-300`}
            >
              <h3 className={`text-xl font-semibold ${theme.primary.text} leading-relaxed`}>
                {faq.question}
              </h3>
              <span className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent text-2xl font-bold`}>
                {openFAQ === index ? '−' : '+'}
              </span>
            </button>
            <motion.div
              key={openFAQ === index ? `open-${index}` : `closed-${index}`}
              initial={{ height: 0, opacity: 0 }}
              animate={openFAQ === index ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div ref={(el) => (contentRefs.current[index] = el)} className={`p-6 pt-0 ${theme.primary.textSecondary} leading-relaxed`}>
                <p>{faq.answer}</p>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>

      {/* Back to Home Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="p-6 md:p-12 mt-8 text-center"
      >
        <Link
          to="/"
          className={`bg-gradient-to-r ${theme.primary.button} text-white px-8 py-4 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 shadow-2xl font-semibold text-lg inline-block`}
        >
          Back to Home
        </Link>
      </motion.div>
    </div>
  );
}

export default FAQPage;
